<template>
  <main class="grow learn-hub">
    <section class="learn-hub-hero">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <h1>Practical guides for running client work well.</h1>
        <p class="learn-hub-lede">
          The Workspace369 Learning Center is a free library of guides for people
          who sell their time and skills to clients. Each guide answers one question
          at the top, then covers getting paid, running the operation, or keeping
          every client relationship in one place, with no account needed.
        </p>
        <p class="learn-hub-count">{{ guideCount }} guides in {{ clusters.length }} collections, plus {{ topics.length }} topic hubs.</p>
        <nav class="learn-hub-jump" aria-label="Collections">
          <a v-for="cluster in clusters" :key="cluster.id" :href="`#learn-${cluster.id}`">{{ cluster.title }}</a>
        </nav>
      </div>
    </section>

    <section class="learn-hub-start" aria-labelledby="learn-start-title">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="learn-hub-split">
          <div class="learn-hub-heading">
            <h2 id="learn-start-title">Start with the job in front of you</h2>
            <p>Not sure which guide to open? Pick the situation closest to yours.</p>
          </div>
          <div class="learn-hub-table">
            <NuxtLink
              v-for="path in startPaths"
              :key="path.to"
              :to="localePath(path.to)"
              class="learn-hub-row"
            >
              <span class="learn-hub-row-main">
                <strong>{{ path.situation }}</strong>
                <span>{{ path.copy }}</span>
              </span>
              <i aria-hidden="true">→</i>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section class="learn-hub-topics" aria-labelledby="learn-topics-title">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="learn-hub-split">
          <div class="learn-hub-heading">
            <h2 id="learn-topics-title">Browse by topic</h2>
            <p>Curated collections of our short guides, templates, and tools by subject.</p>
            <p><NuxtLink :to="localePath('/research')">Looking for evidence? Explore our statistics, benchmarks and downloadable research.</NuxtLink></p>
          </div>
          <div class="learn-hub-table">
            <NuxtLink
              v-for="topic in topics"
              :key="topic.slug"
              :to="localePath(`/learn/topics/${topic.slug}`)"
              class="learn-hub-row"
            >
              <span class="learn-hub-row-main">
                <strong>{{ topic.title }}</strong>
                <span>{{ topic.promise }}</span>
              </span>
              <i aria-hidden="true">→</i>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section
      v-for="cluster in clusters"
      :key="cluster.id"
      class="learn-hub-cluster"
      :aria-labelledby="`learn-${cluster.id}`"
    >
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="learn-hub-split">
          <div class="learn-hub-heading">
            <h2 :id="`learn-${cluster.id}`">{{ cluster.title }}</h2>
            <p>{{ cluster.copy }}</p>
          </div>
          <div class="learn-hub-table">
            <NuxtLink
              v-for="page in pagesByCluster(cluster.id)"
              :key="page.slug"
              :to="localePath(`/learn/${page.slug}`)"
              class="learn-hub-row"
            >
              <span class="learn-hub-row-main">
                <strong>{{ page.h1 }}</strong>
                <span>{{ page.description }}</span>
              </span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section class="learn-hub-kinds" aria-labelledby="learn-kinds-title">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="learn-hub-split">
          <div class="learn-hub-heading">
            <h2 id="learn-kinds-title">Guides, topic hubs, tools and research</h2>
            <p>The Learning Center sits next to four other kinds of help. Each one answers a different sort of question.</p>
          </div>
          <div class="learn-hub-table">
            <NuxtLink
              v-for="kind in helpKinds"
              :key="kind.to"
              :to="localePath(kind.to)"
              class="learn-hub-row"
            >
              <span class="learn-hub-row-main">
                <strong>{{ kind.title }}</strong>
                <span>{{ kind.copy }}</span>
              </span>
              <i aria-hidden="true">→</i>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section class="learn-hub-faq" aria-labelledby="learn-faq-title">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="learn-hub-split">
          <div class="learn-hub-heading">
            <h2 id="learn-faq-title">Learning Center questions</h2>
          </div>
          <div class="learn-hub-table">
            <details v-for="item in faqs" :key="item.q" class="learn-hub-faq-item">
              <summary>{{ item.q }}</summary>
              <p>{{ item.a }}</p>
            </details>
          </div>
        </div>
      </div>
    </section>

    <section class="learn-hub-next" aria-labelledby="learn-next-title">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="learn-hub-split">
          <div class="learn-hub-heading">
            <h2 id="learn-next-title">Turn the playbook into the workspace.</h2>
            <p>
              Each guide maps to the part of Workspace369 built for it — explore the
              solution pages when you are ready to put the system in place.
            </p>
          </div>
          <nav class="learn-hub-table" aria-label="Solution hubs">
            <NuxtLink class="learn-hub-row" :to="localePath('/solutions/workspace-crm')">
              <span class="learn-hub-row-main"><strong>CRM with invoicing</strong></span><i aria-hidden="true">→</i>
            </NuxtLink>
            <NuxtLink class="learn-hub-row" :to="localePath('/solutions/project-profitability-software-for-agencies')">
              <span class="learn-hub-row-main"><strong>Project profitability software</strong></span><i aria-hidden="true">→</i>
            </NuxtLink>
            <NuxtLink class="learn-hub-row" :to="localePath('/solutions/agency-crm')">
              <span class="learn-hub-row-main"><strong>Agency CRM software</strong></span><i aria-hidden="true">→</i>
            </NuxtLink>
            <NuxtLink class="learn-hub-row" :to="localePath('/solutions')">
              <span class="learn-hub-row-main"><strong>Browse every solution</strong></span><i aria-hidden="true">→</i>
            </NuxtLink>
          </nav>
        </div>
      </div>
    </section>

    <Cta03 />
    <PhoneStickyCta label="Learn hub" :location="$route.path" watch=".learn-hub-hero" />
  </main>
</template>

<script setup>
import learnIndex from '#build/page-data/learn-index.mjs';
import { learnTopics } from '~/data/learn-topics';
import { localizedUrl } from '~/data/locales';

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = 'https://workspace369.com';
const canonicalUrl = computed(() => localizedUrl(siteUrl, '/learn', canonicalLocaleCode.value));

// Every guide, generated from the catalog, so a new guide shows up here without editing the hub.
const clusters = learnIndex.clusters.filter((c) => learnIndex.guides.some((page) => page.cluster === c.id));
const topics = learnTopics;
const pagesByCluster = (id) => learnIndex.guides.filter((page) => page.cluster === id);
const listedGuides = clusters.flatMap((cluster) => pagesByCluster(cluster.id));
const guideCount = learnIndex.guides.length;

const startPaths = [
  { situation: 'You are sending your first invoice', copy: 'What an invoice must include, how to lay out line items, and the payment terms to set before you send it.', to: '/learn/how-to-write-an-invoice' },
  { situation: 'A client is paying late', copy: 'Deposits, clear terms, easy ways to pay and scheduled reminders that cut the wait.', to: '/learn/how-to-get-paid-faster' },
  { situation: 'You are pricing a new project', copy: 'When hourly, fixed fee or retainer pricing fits, and how to price from cost instead of guesswork.', to: '/learn/how-to-price-agency-work' },
  { situation: 'You are writing a proposal', copy: 'The sections to include, how to present price options, and the follow-up that closes it.', to: '/learn/how-to-write-a-proposal' },
  { situation: 'A new client has just said yes', copy: 'What to collect before work starts, and how to hand it into the project without asking twice.', to: '/learn/client-intake-checklist' },
  { situation: 'You are choosing a CRM', copy: 'What a CRM does for a service business, and the signs a spreadsheet and inbox are no longer enough.', to: '/learn/what-is-a-crm' },
  { situation: 'A client relationship has stopped working', copy: 'The signs it is time, the steps to end it cleanly, and the final invoice and handover.', to: '/learn/how-to-fire-a-client' },
];

const helpKinds = [
  { title: 'Guides', copy: 'One question per page, answered at the top, with the date it was last reviewed, an FAQ and related reading.', to: '/learn' },
  { title: 'Free tools', copy: 'Calculators, templates and generators for rates, deposits, invoices and follow-ups. No signup.', to: '/tools' },
  { title: 'Research', copy: 'Statistics and benchmarks on client-work businesses, each figure with its source.', to: '/research' },
  { title: 'Video tutorials', copy: 'Short videos that show the steps inside Workspace369, chapter by chapter.', to: '/tutorials' },
  { title: 'Help center', copy: 'Documentation, tutorials and email support for questions about your own workspace.', to: '/help-center' },
];

const faqs = [
  {
    q: 'Are the Learning Center guides free?',
    a: 'Yes. Every guide and topic hub is free to read, and the tools they link to work without an account.',
  },
  {
    q: 'Do I need Workspace369 to use these guides?',
    a: 'No. The guides cover the practice itself, such as writing an invoice, setting payment terms or pricing a project, and apply whatever software you use. Where a guide mentions Workspace369, it shows where that step lives in the product.',
  },
  {
    q: 'How current are the guides?',
    a: 'Each guide shows the date it was last reviewed. For product changes since then, the changelog lists every customer-facing Workspace369 release with its date.',
  },
  {
    q: 'Where do I get help with my own Workspace369 account?',
    a: 'Use the help center. It links the documentation, the video tutorials and email support, which is on every plan.',
  },
];

const metaTitle = 'Learning Center | Workspace369';
const metaDescription =
  'Evergreen Workspace369 guides for client work: invoicing and getting paid, agency pricing and profitability, and CRM and client communication fundamentals.';

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: 'description', content: metaDescription },
    { name: 'twitter:title', content: metaTitle },
    { name: 'twitter:description', content: metaDescription },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:title', content: metaTitle },
    { property: 'og:description', content: metaDescription },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl.value }],
  script: [
    {
      key: 'learn-hub-itemlist-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        numberOfItems: listedGuides.length,
        itemListElement: listedGuides.map((page, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: page.h1,
          url: localizedUrl(siteUrl, `/learn/${page.slug}`, canonicalLocaleCode.value),
        })),
      }),
    },
    {
      key: 'learn-hub-faq-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
/* App tokens (InvoicesAppWindow / the Flutter neutral theme): hairline #e6e6e6, 10px list surface,
   4px tag, #171717 ink. Lists read as the app's own tables: one bordered surface, 1px row dividers. */
.learn-hub {
  --ink: #0a0a0a;
  --ink2: #4f4f4f;
  --ink3: #717171;
  --edge: #e6e6e6;
  --hover: rgb(10 10 10 / 0.04);
  --tag-f: rgb(0 0 0 / 0.067);
  --tag-t: #5e5e5e;
  background: #ffffff;
  color: var(--ink);
}

.dark .learn-hub {
  --ink: #fafafa;
  --ink2: #c2c2c2;
  --ink3: #9a9a9a;
  --edge: #2c2c2c;
  --hover: rgb(250 250 250 / 0.05);
  --tag-f: rgb(255 255 255 / 0.12);
  --tag-t: #bdbdbd;
  background: #121212;
}

.learn-hub-hero {
  padding: 8.5rem 0 2.5rem;
}

.learn-hub-hero h1 {
  max-width: 46rem;
  font-size: clamp(1.9rem, 5.2vw, 3.5rem);
  font-weight: 780;
  line-height: 1.05;
  letter-spacing: -0.01em;
  text-wrap: balance;
}

.learn-hub-lede {
  max-width: 40rem;
  margin-top: 1.1rem;
  color: var(--ink2);
  font-size: clamp(0.95rem, 2.4vw, 1.05rem);
  line-height: 1.7;
}

.learn-hub-count {
  margin-top: 1rem;
  color: var(--ink3);
  font-size: 0.85rem;
}

.learn-hub-jump {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.learn-hub-jump a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 0.9rem;
  border: 1px solid var(--edge);
  border-radius: 6px;
  color: var(--ink);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 120ms ease;
}

.learn-hub-jump a:hover,
.learn-hub-jump a:focus-visible {
  background: var(--hover);
}

/* Jump links land below the fixed site header and offer banner (the router scrolls to the H2). */
.learn-hub-cluster h2 {
  scroll-margin-top: 8.5rem;
}

.learn-hub-start,
.learn-hub-topics,
.learn-hub-cluster,
.learn-hub-kinds,
.learn-hub-faq,
.learn-hub-next {
  border-top: 1px solid var(--edge);
  padding: 2.75rem 0;
}

.learn-hub-split {
  display: grid;
  gap: 1.5rem;
}

.learn-hub-heading h2 {
  font-size: clamp(1.4rem, 2.6vw, 1.75rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.005em;
}

.learn-hub-heading p {
  margin-top: 0.7rem;
  color: var(--ink2);
  font-size: 0.95rem;
  line-height: 1.65;
}

.learn-hub-heading a {
  color: var(--ink);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 0.15rem;
}

.learn-hub-table {
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 10px;
}

.learn-hub-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  border-top: 1px solid var(--edge);
  padding: 0.9rem 1.1rem;
  color: inherit;
  text-decoration: none;
  transition: background-color 120ms ease;
}

.learn-hub-row:first-child {
  border-top: 0;
}

.learn-hub-row:hover,
.learn-hub-row:focus-visible {
  background: var(--hover);
}

.learn-hub-row-main {
  display: grid;
  gap: 0.2rem;
  min-width: 0;
}

.learn-hub-row-main strong {
  font-size: 0.95rem;
  font-weight: 650;
  line-height: 1.35;
}

.learn-hub-row-main > span {
  color: var(--ink2);
  font-size: 0.85rem;
  line-height: 1.5;
}

/* Phones: each guide's description shows two lines; the full text stays in the HTML. */
@media (max-width: 767px) {
  .learn-hub-cluster .learn-hub-row-main > span {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.learn-hub-faq-item {
  border-top: 1px solid var(--edge);
  padding: 0.9rem 1.1rem;
}

.learn-hub-faq-item:first-child {
  border-top: 0;
}

.learn-hub-faq-item summary {
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 650;
  line-height: 1.4;
}

.learn-hub-faq-item p {
  margin-top: 0.5rem;
  color: var(--ink2);
  font-size: 0.9rem;
  line-height: 1.65;
}

.learn-hub-row i {
  color: var(--ink3);
  font-style: normal;
}

@media (min-width: 1024px) {
  .learn-hub-hero {
    padding-top: 9.5rem;
  }

  .learn-hub-split {
    grid-template-columns: minmax(0, 22rem) minmax(0, 1fr);
    gap: 4rem;
  }

  .learn-hub-heading {
    position: sticky;
    top: 6.5rem;
    align-self: start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .learn-hub-row {
    transition: none;
  }
}
</style>
